import { CheckCircle2, CircleAlert, CircleX } from 'lucide-react'
import { cn, formatEventTime } from '@/lib/utils'
import {
  actionResolutionExplanation,
  actionTypeBadge,
  type ActionAuditEvent,
  type ActionItemLike,
} from '@/features/purchase-orders/action-audit-model'
import { useActionEmail } from '@/features/communications/action-email'

/** An action item tied to one email: the email raised it, or the AI resolved it from the email. */
export type MessageActionLink = {
  role: 'raised' | 'resolved'
  action: ActionItemLike
  /** The resolved/dismissed event, once the action is closed. */
  closed: ActionAuditEvent<ActionItemLike> | null
}

/** The links whose action item is still open; resolved and dismissed ones wait for the full email. */
function openActionLinks(links: MessageActionLink[]): MessageActionLink[] {
  return links.filter((link) => link.closed === null)
}

function closedLine(closed: ActionAuditEvent<ActionItemLike>) {
  return (
    <>
      {closed.kind === 'dismissed' ? 'Dismissed' : 'Resolved'}
      {closed.actor && <> by {closed.actor}</>}
      <span aria-hidden> · </span>
      <time dateTime={closed.at}>{formatEventTime(closed.at)}</time>
    </>
  )
}

/** The action items an email raised or resolved, shown as part of that email. */
export function MessageActionItems({
  links,
  compact = false,
}: {
  links: MessageActionLink[]
  /** Collapsed email: open items only, two lines of each summary. */
  compact?: boolean
}) {
  const { renderAction } = useActionEmail()
  const shown = compact ? openActionLinks(links) : links
  if (shown.length === 0) return null
  return (
    <ul className="space-y-1.5" aria-label="Action items from this email">
      {shown.map(({ role, action, closed }) => {
        const open = closed === null
        // An open item this email raised is handled right here, on its full card.
        const card = open && role === 'raised' && !compact ? renderAction?.(action) : null
        if (card) {
          return (
            <li
              key={`${role}-${action.id}`}
              data-testid="message-action-card"
              data-role={role}
              className="border-t pt-3"
            >
              {card}
            </li>
          )
        }
        const explanation = closed && !compact ? actionResolutionExplanation(action) : null
        const Icon = open ? CircleAlert : closed.kind === 'dismissed' ? CircleX : CheckCircle2
        return (
          <li
            key={`${role}-${action.id}`}
            data-testid="message-action-item"
            data-role={role}
            className={cn(
              'flex items-start gap-2 rounded-md px-2.5 py-2 text-xs',
              open
                ? 'bg-amber-50 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100'
                : 'bg-muted/60 text-muted-foreground',
            )}
          >
            <Icon
              aria-hidden
              className={cn(
                'mt-0.5 h-3.5 w-3.5 shrink-0',
                open ? 'text-amber-600' : closed.kind === 'dismissed' ? '' : 'text-green-600',
              )}
            />
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium text-foreground">
                  {role === 'raised' ? 'Raised action item' : 'Resolved action item'}
                </span>
                {action.card ? (
                  <span className="font-medium text-foreground">{action.card.kind_label}</span>
                ) : (
                  actionTypeBadge(action.action_type)
                )}
                {role === 'raised' &&
                  (open ? (
                    <span className="font-medium text-amber-700 dark:text-amber-300">Open</span>
                  ) : (
                    <span>{closedLine(closed)}</span>
                  ))}
                {role === 'resolved' && closed && <span>{closedLine(closed)}</span>}
              </div>
              <p className={cn('break-words text-sm text-foreground', compact && 'line-clamp-2')}>
                {action.card?.headline ?? action.summary}
              </p>
              {explanation && <p className="break-words">{explanation}</p>}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
